import React from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PreviewItem } from '../../../../lib/api/settlementCompleteDateAdjustment';
import {
  PREVIEW_EXCLUDED_REASON_LABEL,
  PreviewExcludedReason,
  SETTLEMENT_ITEM_TYPE_LABEL,
} from '../../constants/labels';
import { StyledAfterCell, StyledBeforeCell, StyledMonthCell } from './styles';

/**
 * PreviewModal 의 4개 탭에서 사용하는 컬럼 정의.
 *
 * `ExecuteTabPanel/columns.tsx` 분리 패턴과 동일 — props 의존이 없는
 * (PreviewItem / 도메인 상수만 참조) 정적 정의이므로 hook 화 없이 const 로 export.
 */

// ─── 탭1: 적용 대상 ─────────────────────────────────
export const APPLIED_COLUMNS: ColumnsType<PreviewItem> = [
  { title: '정산번호', dataIndex: 'id', width: 90, fixed: 'left' },
  { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
  {
    title: '구분',
    dataIndex: 'type',
    width: 110,
    render: (v: PreviewItem['type']) => SETTLEMENT_ITEM_TYPE_LABEL[v] ?? v,
  },
  {
    title: '변경 전',
    className: 'before-group',
    children: [
      {
        title: '정산 완료일',
        dataIndex: 'beforeCompleteDate',
        width: 140,
        className: 'before-cell',
        render: (v?: string) => <StyledBeforeCell>{v ?? '-'}</StyledBeforeCell>,
      },
    ],
  },
  {
    title: '변경 후',
    className: 'after-group',
    children: [
      {
        title: '정산 완료일',
        dataIndex: 'afterCompleteDate',
        width: 140,
        className: 'after-cell',
        render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
      },
    ],
  },
  {
    title: '월 경계 변경',
    key: 'monthChange',
    width: 140,
    render: (_: unknown, r: PreviewItem) =>
      r.monthChange ? (
        <Tag color="orange">
          {r.monthChange.from} → {r.monthChange.to}
        </Tag>
      ) : null,
  },
];

// ─── 탭2: 제외 대상 ─────────────────────────────────
export const EXCLUDED_COLUMNS: ColumnsType<{ id: number; reason: string }> = [
  { title: '정산번호', dataIndex: 'id', width: 140 },
  {
    title: '제외 사유',
    dataIndex: 'reason',
    render: (v: string) => (
      <Tag color="default">
        {PREVIEW_EXCLUDED_REASON_LABEL[v as PreviewExcludedReason] ?? v}
      </Tag>
    ),
  },
];

// ─── 탭3: 월 변경 영향 ───────────────────────────
// before/after 의 YYYY-MM 이 달라진 행만 모아 회계 영향 한 화면 확인.
// 같은 정산이라도 4월 → 5월로 넘어가면 손익/세금 인식 시점이 바뀌어 운영자 검토 필요.
export const MONTH_CHANGED_COLUMNS: ColumnsType<PreviewItem> = [
  { title: '정산번호', dataIndex: 'id', width: 90 },
  { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
  {
    title: '변경 전 완료일',
    dataIndex: 'beforeCompleteDate',
    width: 140,
    render: (v?: string) => <StyledBeforeCell>{v ?? '-'}</StyledBeforeCell>,
  },
  {
    title: '변경 전 월',
    key: 'beforeMonth',
    width: 120,
    render: (_: unknown, r: PreviewItem) =>
      r.monthChange ? <StyledBeforeCell>{r.monthChange.from}</StyledBeforeCell> : '-',
  },
  {
    title: '변경 후 완료일',
    dataIndex: 'afterCompleteDate',
    width: 140,
    render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
  },
  {
    title: '변경 후 월',
    key: 'afterMonth',
    width: 120,
    render: (_: unknown, r: PreviewItem) =>
      r.monthChange ? <StyledMonthCell>{r.monthChange.to}</StyledMonthCell> : '-',
  },
];

// ─── 탭4: 활성 배치 중복 ─────────────────────────
export const ACTIVE_BATCH_COLUMNS: ColumnsType<PreviewItem> = [
  { title: '정산번호', dataIndex: 'id', width: 90 },
  { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
  {
    title: '변경 후 정산 완료일',
    dataIndex: 'afterCompleteDate',
    width: 160,
    render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
  },
  {
    title: '이미 적용 중인 배치 ID',
    dataIndex: 'activeBatchId',
    ellipsis: true,
    render: (v?: string) => (v ? <Tag color="orange">{v}</Tag> : '-'),
  },
];
